import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import supabase from '../supabase';
import '../design-system.css';
import { useSiteLang as useLang } from '../siteLang';
import React, { useEffect } from 'react';

const t = {
    fr: {
        eyebrow: 'winston.ie', title: 'Reste connecté',
        subtitle: 'Reçois nos nouveaux albums, posts et annonces directement par courriel.',
        emailLabel: 'Email', placeholder: 'toi@example.com',
        consent: (link) => <>J'accepte de recevoir des communications par courriel de winston.ie et je consens à la collecte de mon adresse à cette fin, conformément à la {link}.</>,
        privacyLink: 'politique de confidentialité',
        submit: "S'inscrire", submitting: 'Inscription...',
        noSpam: 'Pas de spam, promis. Désabonnement possible à tout moment.',
        errInvalid: 'Entre un email valide.', errConsent: 'Le consentement est requis pour t\'inscrire.',
        errDup: 'Cet email est déjà inscrit.', errGeneric: 'Une erreur est survenue. Réessaie.',
        doneTitle: 'Vérifie ta boîte mail', doneBody: 'Un lien de confirmation vient de t\'être envoyé. Clique dessus pour activer ton inscription.',
    },
    en: {
        eyebrow: 'winston.ie', title: 'Stay connected',
        subtitle: 'Get our new albums, posts and announcements straight to your inbox.',
        emailLabel: 'Email', placeholder: 'you@example.com',
        consent: (link) => <>I agree to receive email communications from winston.ie and consent to the collection of my address for this purpose, in accordance with the {link}.</>,
        privacyLink: 'privacy policy',
        submit: 'Subscribe', submitting: 'Subscribing...',
        noSpam: 'No spam, promise. Unsubscribe anytime.',
        errInvalid: 'Enter a valid email.', errConsent: 'Consent is required to subscribe.',
        errDup: 'This email is already subscribed.', errGeneric: 'Something went wrong. Try again.',
        doneTitle: 'Check your inbox', doneBody: 'A confirmation link was just sent to you. Click it to activate your subscription.',
    },
    vi: {
        eyebrow: 'winston.ie', title: 'Luôn kết nối',
        subtitle: 'Nhận album mới, bài viết và thông báo trực tiếp qua email.',
        emailLabel: 'Email', placeholder: 'ban@example.com',
        consent: (link) => <>Tôi đồng ý nhận thông tin qua email từ winston.ie và đồng ý cho việc thu thập địa chỉ của tôi cho mục đích này, theo {link}.</>,
        privacyLink: 'chính sách bảo mật',
        submit: 'Đăng ký', submitting: 'Đang đăng ký...',
        noSpam: 'Không spam, cam kết. Có thể hủy đăng ký bất cứ lúc nào.',
        errInvalid: 'Nhập email hợp lệ.', errConsent: 'Cần có sự đồng ý để đăng ký.',
        errDup: 'Email này đã được đăng ký.', errGeneric: 'Đã xảy ra lỗi. Vui lòng thử lại.',
        doneTitle: 'Kiểm tra hộp thư của bạn', doneBody: 'Một liên kết xác nhận vừa được gửi. Nhấp vào đó để kích hoạt đăng ký của bạn.',
    },
};

function NewsletterSignup() {
    const { lang } = useLang();
    const copy = t[lang] || t.fr;

    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [honeypot, setHoneypot] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (honeypot) { setDone(true); return; }
        if (!email.includes('@')) { setError(copy.errInvalid); return; }
        if (!consent) { setError(copy.errConsent); return; }

        setLoading(true);

        // Vérifie d'abord si l'email existe déjà (ex: désabonné qui revient)
        const { data: existing } = await supabase
            .from('newsletter_subscribers')
            .select('id, unsubscribed, confirmed')
            .eq('email', email)
            .maybeSingle();

        let insertError;

        if (existing) {
            if (!existing.unsubscribed && existing.confirmed) {
                // Déjà inscrit et actif — rien à faire
                setLoading(false);
                setError(copy.errDup);
                return;
            }

            // Réactive l'abonnement — génère un nouveau token de confirmation
            const newToken = crypto.randomUUID();
            const { error: updateError } = await supabase
                .from('newsletter_subscribers')
                .update({
                    unsubscribed: false,
                    confirmed: false,
                    consent_given: true,
                    consent_at: new Date().toISOString(),
                    confirm_token: newToken,
                })
                .eq('id', existing.id);
            insertError = updateError;
        } else {
            const { error: newInsertError } = await supabase
                .from('newsletter_subscribers')
                .insert({
                    email,
                    confirmed: false,
                    unsubscribed: false,
                    consent_given: true,
                    consent_at: new Date().toISOString(),
                });
            insertError = newInsertError;
        }

        if (insertError) {
            setLoading(false);
            setError(copy.errGeneric);
            return;
        }

        // Déclenche l'envoi du courriel de confirmation
        await supabase.functions.invoke('confirm-newsletter', { body: { email } });

        setLoading(false);
        setDone(true);
    };

    useEffect(() => { document.title = 'Newsletter | winston.ie'; }, []);

    if (done) {
        return (
            <div className="w-page">
                <Navbar variant="light" />
                <div className="w-body" style={{ maxWidth: '440px', textAlign: 'center', paddingTop: '80px' }}>
                    <p className="w-eyebrow">{copy.eyebrow}</p>
                    <h1 className="w-title">{copy.doneTitle}</h1>
                    <p className="w-subtitle">{copy.doneBody}</p>
                </div>
                <Footer variant="light" />
            </div>
        );
    }

    return (
        <div className="w-page">
            <Navbar variant="light" />

            <div className="w-body" style={{ maxWidth: '420px' }}>
                <p className="w-eyebrow">{copy.eyebrow}</p>
                <h1 className="w-title" style={{ marginBottom: '4px' }}>{copy.title}</h1>
                <p className="w-subtitle" style={{ marginBottom: '32px' }}>{copy.subtitle}</p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <input
                        type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
                        tabIndex="-1" autoComplete="off"
                        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                    />

                    <div className="w-field-group" style={{ marginBottom: 0 }}>
                        <p className="w-field-label">{copy.emailLabel}</p>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-field-input" placeholder={copy.placeholder} required />
                    </div>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12.5px', color: 'var(--w-ink-soft)', cursor: 'pointer', lineHeight: 1.5 }}>
                        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: '2px', flexShrink: 0 }} required />
                        <span>
                            {copy.consent(
                                <span onClick={() => window.open('/privacy', '_blank')} style={{ color: 'var(--w-accent)', textDecoration: 'underline', cursor: 'pointer' }}>
                                    {copy.privacyLink}
                                </span>
                            )}
                        </span>
                    </label>

                    {error && <p style={{ color: '#a3382f', fontSize: '12.5px', margin: 0 }}>{error}</p>}

                    <button type="submit" className="w-btn-primary" disabled={loading}>
                        {loading ? copy.submitting : copy.submit}
                    </button>
                </form>

                <p style={{ fontSize: '11px', color: 'var(--w-muted)', marginTop: '20px', textAlign: 'center' }}>{copy.noSpam}</p>
            </div>

            <Footer variant="light" />
        </div>
    );
}

export default NewsletterSignup;